import { ReactNode, useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "../../../../assets/icons";
import styles from "./Dropdown.module.scss";

export interface DropdownOption {
	value: string;
	label: ReactNode;
}

interface DropdownProps {
	options: DropdownOption[];
	value: string | null;
	onChange: (value: string) => void;
	placeholder: string;
	disabled?: boolean;
	emptyText?: string;
}

const Dropdown =({ options, value, onChange, placeholder, disabled, emptyText = "No items found" }: DropdownProps) => {
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [open]);

	const selected = options.find(option => option.value === value);

	const handleSelect = (optionValue: string) => {
		onChange(optionValue);
		setOpen(false);
	};

	return (
		<div className={styles.container} ref={containerRef}>
			<button
				type="button"
				className={styles.trigger}
				onClick={() => !disabled && setOpen(prev => !prev)}
				disabled={disabled}
			>
				<span className={`${styles.text} ${selected ? "" : styles.placeholder}`}>{selected ? selected.label : placeholder}</span>
				<span className={styles.chevron}>
					<ChevronDownIcon />
				</span>
			</button>

			{open ? (
				<div className={styles.menu}>
					{options.length === 0 ? (
						<div className={styles.empty}>{emptyText}</div>
					) : (
						options.map(option => (
							<button
								type="button"
								key={option.value}
								className={`${styles.option} ${option.value === value ? styles.optionSelected : ""}`}
								onClick={() => handleSelect(option.value)}
							>
								{option.label}
							</button>
						))
					)}
				</div>
			) : null}
		</div>
	);
};

export default Dropdown;
